import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { Quote } from '@app/_components/quote';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { SharedService } from '../_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class QuoteService {

	statuses: any = [
		{ value: '0', name: 'Active' },
		{ value: '2', name: 'Suspended' },
		{ value: '1', name: 'Canceled' }
	];


	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService
		) { }

	urlEndpoint = 'quotes';
  urlEndpoint1 = 'quoteDetails';

	quoteDiscPct: any[] =
		[
			{ value: 0.01, name: '-1' },
			{ value: 0.02, name: '-2' },
			{ value: 0.03, name: '-3' },
			{ value: 0.04, name: '-4' },
			{ value: 0.05, name: '-5' },
			{ value: 0.06, name: '-6' },
			{ value: 0.07, name: '-7' },
			{ value: 0.08, name: '-8' },
			{ value: 0.09, name: '-9' },
			{ value: 0.10, name: '-10' },
			{ value: 0.11, name: '-11' },
			{ value: 0.12, name: '-12' },
			{ value: 0.13, name: '-13' },
			{ value: 0.14, name: '-14' },
			{ value: 0.15, name: '-15' },
			{ value: 0.16, name: '-16' },
			{ value: 0.17, name: '-17' },
			{ value: 0.18, name: '-18' },
			{ value: 0.19, name: '-19' },
			{ value: 0.20, name: '-20' },
		];

	add(quote: Quote): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(quote));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/create`, quote).pipe();
	}

	/**
	 * Bulk-price multiple candidate stay windows in one round-trip. Used by
	 * the Smart-Parse flex-date finder on the tape chart so each card shows
	 * the authoritative grandTotalWithTax (same number Build Quote produces)
	 * instead of a client-side pre-tax estimate.
	 *
	 * `items` is the same shape the existing /rate-quotes/generate-quote
	 * endpoint accepts: { shortTermRentalId, checkInDate (YYYY-MM-DD),
	 * checkOutDate (YYYY-MM-DD), numAdults, numChildren, numberOfNightsStay }.
	 *
	 * Per-item failures don't fail the whole call — the response includes
	 * status=OK or status=ERROR for each row.
	 */
	bulkTotals(items: any[]): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(
			`${environment.apiUrl}/${this.urlEndpoint}/bulk-totals`,
			{ items }
		);
	}

	update(quote: Quote): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, quote).pipe();
	}

	delete(developmentId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${developmentId}`);
	}

	findAll(): Observable<ApiResponse> {
		 return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint1}/summary`);
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		 return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint1}/summary`, { params: params });

	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`);
	}

	/**
	 * Polls the backend for the latest email-delivery outcome for a given quote.
	 * The quote-create flow is fire-and-forget, so the HTTP 200 response arrives
	 * before SendGrid has been called.  Call this ~5 s after save to learn whether
	 * delivery succeeded (status 2) or failed (status 4).
	 * Response shape: { data: { emailStatus: { emailSendStatus, emailSendStatusMsg } } }
	 */
	getEmailStatus(quoteId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${quoteId}/email-status`);
	}

	findByQuoteId(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint1}/${id}`);
	}

	findPictureByPropertyId(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/pictures/shortTermRental/${id}`);
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}


	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
}
